import Big from 'big.js';

import { multiSplit } from './string';

export type Decimal = {
  add: DecimalOp;
  decimalInternal: Big;
  divide: DecimalOp;
  equal: DecimalPredicate;
  greaterThan: DecimalPredicate;
  greaterThanOrEqual: DecimalPredicate;
  lessThan: DecimalPredicate;
  lessThanOrEqual: DecimalPredicate;
  multiply: DecimalOp;
  subtract: DecimalOp;
  out: () => string;
  toNumber: () => number;
  toString: () => string;
};

type DecimalInput = Big | Decimal | string | number | null | undefined;

type DecimalOp = (value: DecimalInput) => Decimal;

type DecimalPredicate = (value: DecimalInput) => boolean;

const DECIMAL_SEPARATORS = ['.', ','];

const NEGATIVE_SIGN = '-';

const TOTAL_DECIMALS = 2;

const isDecimal = (value: DecimalInput): value is Decimal =>
  typeof value === 'object' && !!value && 'decimalInternal' in value;

const normalizeInput = (input: Exclude<DecimalInput, Big | Decimal>) => {
  if (!input || input === NEGATIVE_SIGN) return 0;
  if (typeof input === 'number') return input;

  const parts = multiSplit(input, DECIMAL_SEPARATORS);
  const wholeOrAfterDecimal = parts.pop();
  const wholeOrBeforeDecimal = parts.join('');

  if (!wholeOrAfterDecimal) return wholeOrBeforeDecimal || 0;
  if (!wholeOrBeforeDecimal) return wholeOrAfterDecimal || 0;

  return `${wholeOrBeforeDecimal}.${wholeOrAfterDecimal}`;
};

const inputToBig = (input: DecimalInput) => {
  try {
    let toConvert;

    if (input instanceof Big) toConvert = input;
    else if (isDecimal(input)) toConvert = input.decimalInternal;
    else toConvert = normalizeInput(input);

    return new Big(toConvert);
  } catch (err) {
    throw new Error(`Failed to convert ${input} to number`, { cause: err });
  }
};

const toDecimal = (decimalInput?: DecimalInput): Decimal => {
  const bigMainValue = inputToBig(decimalInput);

  const createOp =
    (op: (a: Big, b: Big) => Big): DecimalOp =>
    (value) => {
      const bigOpValue = inputToBig(value);

      try {
        const bigResValue = op(bigMainValue, bigOpValue);

        return toDecimal(bigResValue);
      } catch (err) {
        throw new Error('Failed to perform decimal operation', { cause: err });
      }
    };

  const createPredicate =
    (predicate: (a: Big, b: Big) => boolean): DecimalPredicate =>
    (value) => {
      const bigToCompareValue = inputToBig(value);

      try {
        return predicate(bigMainValue, bigToCompareValue);
      } catch (err) {
        throw new Error('Failed to compare decimal values', { cause: err });
      }
    };

  return {
    add: createOp((a, b) => a.plus(b)),
    decimalInternal: bigMainValue,
    divide: createOp((a, b) => a.div(b)),
    equal: createPredicate((a, b) => a.eq(b)),
    greaterThan: createPredicate((a, b) => a.gt(b)),
    greaterThanOrEqual: createPredicate((a, b) => a.gte(b)),
    lessThan: createPredicate((a, b) => a.lt(b)),
    lessThanOrEqual: createPredicate((a, b) => a.lte(b)),
    multiply: createOp((a, b) => a.times(b)),
    out: () => bigMainValue.toFixed(TOTAL_DECIMALS),
    subtract: createOp((a, b) => a.minus(b)),
    toNumber: () => bigMainValue.toNumber(),
    toString: () => bigMainValue.toString(),
  };
};

export const numbersAreEqual = (a: DecimalInput, b: DecimalInput) =>
  toDecimal(a).out() === toDecimal(b).out();

export default toDecimal;
